import { isDefined } from "@clipboard-health/util-ts";
import { useIsArrivalTimeEligible } from "@src/appV2/redesign/ArrivalTime/useIsArrivalTimeEligible";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { differenceInMinutes, parseISO } from "date-fns";
import { useParams } from "react-router-dom";

import { useShiftInviteSnapshot } from "../hooks/useShiftInviteSnapshot";
import { type ShiftInvite } from "../types";
import { ShiftInviteRegularShiftBookedBottomSheetContainer } from "./RegularShiftBookedBottomSheetContainer";
import { ShiftInviteShortLeadTimeShiftBookedBottomSheetContainer } from "./ShortLeadTimeShiftBookedBottomSheetContainer";

interface ShiftInviteBookedBottomSheetContainerProps {
  modalState: UseModalState;
  shiftInvite?: ShiftInvite;
}

export function ShiftInviteBookedBottomSheetContainer(
  props: ShiftInviteBookedBottomSheetContainerProps
) {
  const { modalState, shiftInvite: shiftInviteFromProps } = props;
  const { shiftInviteId: shiftInviteIdFromParams } = useParams<{ shiftInviteId: string }>();

  const shiftInviteId = shiftInviteFromProps?.id ?? shiftInviteIdFromParams ?? "";
  const { shiftInviteSnapshot } = useShiftInviteSnapshot(shiftInviteId);
  const { shiftInvite, computedArrivalData } = shiftInviteSnapshot ?? {};

  const isShortLeadTime = useIsArrivalTimeEligible({
    shiftStartIso: shiftInvite?.attributes.shiftDetails.start,
    workplaceType: shiftInvite?.attributes.facility.type,
  });

  if (!isDefined(shiftInvite)) {
    return null;
  }

  const shouldAlreadyLeave =
    isDefined(computedArrivalData) &&
    isDefined(shiftInvite) &&
    (parseISO(shiftInvite.attributes.shiftDetails.start) < computedArrivalData.targetArrivalTime ||
      differenceInMinutes(computedArrivalData.departureTime, new Date()) <=
        computedArrivalData.gracePeriodInMinutes);

  return isShortLeadTime && shouldAlreadyLeave ? (
    <ShiftInviteShortLeadTimeShiftBookedBottomSheetContainer
      modalState={modalState}
      shiftInvite={shiftInvite}
    />
  ) : (
    <ShiftInviteRegularShiftBookedBottomSheetContainer
      modalState={modalState}
      shiftInvite={shiftInvite}
    />
  );
}
